import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

import { AlertTriangle, ArrowRight, Loader2 } from "lucide-react";

const REQUIRED_FIELDS = ["full_name", "company_name", "company_size", "location"] as const;

export const SupplierProfileGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, role } = useAuth();
  
  const location = useLocation();
  const [complete, setComplete] = useState<boolean | null>(null);
  const lastCheckRef = useRef<string | null>(null);

  const isAdmin = role === "admin";

  const check = useCallback(async () => {
    if (!user || isAdmin) {
      setComplete(true);
      return;
    }
    // Only re-check if coming from the profile page (where they might have updated)
    const cacheKey = user.id;
    if (lastCheckRef.current === cacheKey && complete !== null && location.pathname !== "/supplier/profile") {
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select("full_name, company_name, company_size, location")
      .eq("user_id", user.id)
      .maybeSingle();
    if (!data) { setComplete(false); return; }
    const filled = REQUIRED_FIELDS.every(f => data[f] && String(data[f]).trim().length > 0);
    setComplete(filled);
    lastCheckRef.current = cacheKey;
  }, [user, isAdmin, location.pathname, complete]);

  useEffect(() => {
    check();
  }, [check]);

  if (complete === null) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      {!complete && location.pathname !== "/supplier/profile" && (
        <div className="mx-6 mt-4 mb-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
          <p className="text-sm text-foreground flex-1">
            Please complete your company profile to get the most out of the platform.
          </p>
          <Link
            to="/supplier/profile"
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            Complete Profile <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}
      {children}
    </>
  );
};
